/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { Fragment } from 'react';
import { splitMentions, type Person } from './mention';
import { parseBlocks, parseInline } from './format';

/**
 * A note as it was formatted: paragraphs and lists, with bold, italic and strike
 * drawn and mentions picked out inside them.
 *
 * Drawn from React elements, never from a string of HTML, so nothing a note
 * contains can become markup. `format.ts` owns the grammar; this only draws it.
 */
export function RichText({ text, people }: { text: string; people: Person[] }) {
  return (
    <>
      {parseBlocks(text).map((block, i) => {
        if (block.kind === 'text') return <Runs key={i} text={block.text} people={people} />;
        const items = block.items.map((item, j) => (
          <li key={j}><Runs text={item} people={people} /></li>
        ));
        return block.kind === 'ul'
          ? <ul key={i} className="m-0 list-disc pl-5">{items}</ul>
          : <ol key={i} start={block.start} className="m-0 list-decimal pl-5">{items}</ol>;
      })}
    </>
  );
}

/** One line's runs of styled text. Mentions are found *inside* each run. */
function Runs({ text, people }: { text: string; people: Person[] }) {
  return (
    <>
      {parseInline(text).map((run, i) => {
        let node: React.ReactNode = <MessageText text={run.text} people={people} />;
        if (run.bold) node = <strong>{node}</strong>;
        if (run.italic) node = <em>{node}</em>;
        if (run.strike) node = <s>{node}</s>;
        return <Fragment key={i}>{node}</Fragment>;
      })}
    </>
  );
}

/**
 * A message, with its mentions picked out.
 *
 * **Only names the server would also have recognised.** `splitMentions` matches
 * against the same roster the server used, so a highlighted name is one somebody
 * was genuinely notified about. Highlighting anything after an `@` would be the
 * app claiming to have told a person it never told.
 */
function MessageText({ text, people }: { text: string; people: Person[] }) {
  const parts = splitMentions(text, people);
  return (
    <>
      {parts.map((part, i) => (part.mention ? (
        <span key={i} className="mention rounded bg-action-bg px-1 font-semibold text-action">
          {part.text}
        </span>
      ) : (
        <span key={i}>{part.text}</span>
      )))}
    </>
  );
}

/**
 * A note on **one line**, for places that have room for a single row — the bell.
 *
 * Lists cannot be drawn as lists in a row, so each item becomes a bullet inline,
 * and line breaks become spaces. Bold, italic and strike are drawn as they are in
 * the thread. No mentions are picked out: this has no roster to match against,
 * and a highlight the app cannot vouch for is the thing `splitMentions` exists to
 * avoid.
 *
 * Plain text passes through untouched, which matters: the bell also holds notes
 * from before there was any formatting, and notes too long to show whole, which
 * the server sends as plain words.
 */
export function InlineNote({ text }: { text: string }) {
  const oneLine = text.replace(/^- /gm, '• ').replace(/\s*\n+\s*/g, ' ');
  return (
    <>
      {parseInline(oneLine).map((run, i) => {
        let node: React.ReactNode = run.text;
        if (run.bold) node = <strong>{node}</strong>;
        if (run.italic) node = <em>{node}</em>;
        if (run.strike) node = <s>{node}</s>;
        return <Fragment key={i}>{node}</Fragment>;
      })}
    </>
  );
}
